import { useTranslation } from "react-i18next";

/**
 * CoachCard — Today screen, coach's latest note.
 *
 * Pure presentational atom. Renders a compact card with:
 *   - small circular avatar (1-letter initials, gradient fallback when
 *     no avatar_url supplied),
 *   - tag with coach name + role + relative time of the note,
 *   - free-form note body (multi-line clamp-free; the design source caps
 *     at ~4 lines visually but we let CSS reflow without truncation),
 *   - optional Reply / Acknowledge CTAs.
 *
 * Relative-time formatting is delegated to the caller via the
 * `relativeTime` prop — keeping i18n / locale-aware "2 ч назад" formatting
 * out of this atom. (DashboardPage already owns dateUtils + i18n.)
 *
 * Empty-state (no note) renders a soft prompt that the coach hasn't
 * written anything today — different from the absent-coach case which
 * should be handled by the parent (don't mount CoachCard at all).
 */

export interface CoachCardProps {
  coachName: string | null;
  /** Pre-translated relative time, e.g. "2 ч назад", "вчера". */
  relativeTime?: string | null;
  /** Coach's note body. When empty/null → empty-state. */
  note: string | null;
  avatarUrl?: string | null;
  /** Role label, default "Тренер". */
  roleLabel?: string | null;
  /** Acknowledged=true paints the Acknowledge CTA in muted "done" state. */
  acknowledged?: boolean;
  onReply?: () => void;
  onAcknowledge?: () => void;
  className?: string;
}

function getInitial(name: string | null): string {
  if (!name) return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed.charAt(0).toUpperCase();
}

export function CoachCard({
  coachName,
  relativeTime,
  note,
  avatarUrl,
  roleLabel,
  acknowledged,
  onReply,
  onAcknowledge,
  className = "",
}: CoachCardProps) {
  const { t } = useTranslation("dashboard");
  const initial = getInitial(coachName);
  const role = roleLabel ?? t("coach_card.role", { defaultValue: "Тренер" });

  if (!note || !note.trim()) {
    return (
      <div
        data-testid="coach-card-empty"
        className={`indigo-card p-5 ${className}`}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {coachName
            ? `${coachName.toUpperCase()} · ${role.toUpperCase()}`
            : role.toUpperCase()}
        </div>
        <div className="mt-2 text-sm text-fg-secondary">
          {t("coach_card.empty", { defaultValue: "Сегодня тренер ещё не оставил заметку." })}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="coach-card"
      data-acknowledged={acknowledged ? "true" : "false"}
      className={`indigo-card p-5 flex flex-col gap-3 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        {avatarUrl ? (
          <img
            data-testid="coach-card-avatar-img"
            src={avatarUrl}
            alt=""
            className="w-7 h-7 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div
            data-testid="coach-card-avatar-fallback"
            className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0"
          >
            <span className="text-[11px] font-bold text-accent">{initial}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-accent truncate">
            {coachName ? `${coachName} · ${role}` : role}
          </div>
          {relativeTime && (
            <div className="text-[8.5px] uppercase tracking-[0.14em] text-fg-muted font-semibold mt-0.5">
              {relativeTime}
            </div>
          )}
        </div>
      </div>

      <div
        data-testid="coach-card-note"
        className="text-[13px] text-fg-primary leading-[1.55] whitespace-pre-wrap"
      >
        {note}
      </div>

      {(onReply || onAcknowledge) && (
        <div className="pt-3 border-t border-bg-border/60 flex gap-2">
          {onReply && (
            <button
              type="button"
              data-testid="coach-card-reply"
              onClick={onReply}
              aria-label={t("coach_card.cta_reply_aria", { defaultValue: "Ответить тренеру" })}
              className="flex-1 px-0 py-2.5 rounded-md bg-accent/15 border border-accent/35 text-accent font-semibold text-[12.5px] hover:bg-accent/25 transition"
            >
              {t("coach_card.cta_reply", { defaultValue: "Ответить" })}
            </button>
          )}
          {onAcknowledge && (
            <button
              type="button"
              data-testid="coach-card-ack"
              onClick={onAcknowledge}
              disabled={acknowledged}
              aria-label={t("coach_card.cta_ack_aria", { defaultValue: "Отметить сообщение прочитанным" })}
              aria-pressed={acknowledged}
              className={`px-3 py-2.5 rounded-md border text-[12px] transition ${
                acknowledged
                  ? "border-ok/35 text-ok cursor-default"
                  : "border-bg-border text-fg-secondary hover:bg-bg-raised"
              }`}
            >
              {acknowledged
                ? t("coach_card.cta_acknowledged", { defaultValue: "Принято" })
                : t("coach_card.cta_acknowledge", { defaultValue: "Принять" })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
